import React, { useEffect, useRef, useState } from "react";
import { Box, Modal, TextField, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import Calendar from "./Calender";
import { FlightServices } from "../../services/FlightServices/FlightServices";
import { useAlert } from "../../services/customHooks/useAlert";

interface PriceCalenderProps {
  value: Dayjs | null;
  onChange: (value: any) => void;
  label?: string;
  originCode: string;
  destinationCode: string;
  className?: string;
  onBlur?: () => void;
  isError?: boolean | undefined;
  errorText?: string | undefined;
  isRequired?: boolean;
  fromDate?: Dayjs | null;
  disabled?: boolean; // ✅ NEW PROP
}

const PriceCalender: React.FC<PriceCalenderProps> = ({
  value,
  onChange,
  label = "Select Date",
  originCode,
  destinationCode,
  className = "",
  errorText,
  isError,
  onBlur,
  isRequired,
  fromDate,
  disabled = false, // ✅ default false
}) => {
  const { showAlert } = useAlert();
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarData, setCalendarData] = useState<any[]>([]);
  const ref = useRef<HTMLDivElement>(null);

  const getCalendar = async () => {
    const payload = {
      Date: new Date(),
      Destination: destinationCode,
      Months: 3,
      Origin: originCode,
    };

    try {
      const data = await FlightServices.getFlightRateAccToDate(payload);
      if (data?.data?.lstMonths) {
        setCalendarData(data.data.lstMonths);
      } else {
        showAlert({
          title: "Error",
          text: data?.data?.error?.Description || "Failed to fetch data",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Error loading calendar", error);
    }
  };

  useEffect(() => {
    if (originCode && destinationCode) {
      getCalendar();
    }
  }, [originCode, destinationCode]);

  const renderLabel = () => (
    <>
      {label}
      {isRequired && <span style={{ color: "red" }}> *</span>}
    </>
  );

  return (
    <div className={`datepicker-wrapper ${className}`} ref={ref}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          open={false}
          value={value}
          label={renderLabel()}
          slotProps={{
            textField: {
              fullWidth: true,
              error: isError,
              helperText: errorText,
              onBlur: onBlur,
              disabled: disabled, // ✅ apply disabled
            },
          }}
          slots={{
            field: (params) => (
              <TextField
                {...params}
                value={value?.format("DD/MM/YYYY") || ""}
                onClick={() => {
                  if (!disabled) {
                    setShowCalendar(true);
                  }
                }}
                disabled={disabled} // ✅ disable input completely
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: isError ? "#d32f2f" : undefined,
                    },
                    "&:hover fieldset": {
                      borderColor: isError ? "#d32f2f" : undefined,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: isError ? "#d32f2f" : "#1976d2",
                    },
                  },
                }}
              />
            ),
          }}
        />
      </LocalizationProvider>

      <div
        style={{
          position: "absolute",
          fontSize: "13px",
          color: "#d32f2f",
          marginLeft: "6px",
          fontWeight: "400",
          marginTop: "-4px",
        }}
      >
        <span>{errorText}</span>
      </div>

      {showCalendar && (
        <Modal
          className="calender-bg"
          open={showCalendar}
          onClose={() => setShowCalendar(false)}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box>
            <Typography className="absolute price-calender">
              <Calendar
                calenderData={calendarData}
                onDateSelect={(date) => {
                  onChange(date); // 🔁 send back to Formik
                  setShowCalendar(false);
                }}
                fromDate={fromDate ? fromDate.format("YYYY-MM-DD") : ""}
              />
            </Typography>
          </Box>
        </Modal>
      )}
    </div>
  );
};

export default PriceCalender;
